import { useState, useEffect } from "react";
import { useTheme } from "@hooks/useTheme";

const TabsContainer = ({
  tabs,
  defaultActiveTab = 0,
  className = "",
  onTabChange,
  activeTab: controlledActiveTab,
  dataTestId,
  compact = false,
  renderTabContent,
}) => {
  const { colors } = useTheme();
  
  // Use controlled active tab if provided, otherwise use internal state
  const [internalActiveTab, setInternalActiveTab] = useState(() => {
    try {
      return Math.max(0, Math.min(defaultActiveTab || 0, (tabs?.length || 1) - 1));
    } catch (error) {
      console.error('Error setting initial active tab:', error);
      return 0;
    }
  });

  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;

  useEffect(() => {
    try {
      const safeIndex = Math.max(0, Math.min(defaultActiveTab || 0, (tabs?.length || 1) - 1));
      if (controlledActiveTab === undefined) {
        setInternalActiveTab(safeIndex);
      }
    } catch (error) {
      console.error('Error updating active tab:', error);
      if (controlledActiveTab === undefined) {
        setInternalActiveTab(0);
      }
    }
  }, [defaultActiveTab, tabs?.length, controlledActiveTab]);

  const handleTabChange = (index) => {
    if (controlledActiveTab === undefined) {
      setInternalActiveTab(index);
    }
    if (onTabChange) {
      onTabChange(index);
    }
  };

  // Safety check for tabs
  if (!tabs || !Array.isArray(tabs) || tabs.length === 0) {
    return (
      <div className={`w-full ${className}`} data-testid={dataTestId}>
        <div className={`text-center py-8 ${colors.textMuted}`} data-testid={dataTestId ? `${dataTestId}-empty` : undefined}>
          No tabs available
        </div>
      </div>
    );
  }

  // Ensure activeTab is within valid range
  const safeActiveTab = Math.max(0, Math.min(activeTab, tabs.length - 1));

  const tabId = (index) => (dataTestId ? `${dataTestId}-tab-${index}` : `tab-${index}`);
  const panelId = (index) => (dataTestId ? `${dataTestId}-panel-${index}` : `tabpanel-${index}`);

  const tabListClassName = compact
    ? `h-8 rounded-2xl px-0.5 py-0.5 ${colors.bgSecondary}`
    : `h-12 rounded-4xl px-1 py-1 ${colors.bgSecondary}`;

  const tabButtonClassName = (isActive) => {
    const stateClass = isActive
      ? `${colors.textActive} ${colors.bgActiveTab}`
      : `${colors.textSubTitle} border-transparent ${colors.hoverTextPrimary}`;

    if (compact) {
      return `tab-button relative flex h-7 min-w-0 flex-1 items-center justify-center rounded-full px-2 py-1 text-xs font-medium transition-colors duration-200 ${stateClass}`;
    }

    return `tab-button relative flex h-10 items-center rounded-full px-6 py-2 text-sm font-medium transition-colors duration-200 ${stateClass}`;
  };

  const tabLabelClassName = (isActive) =>
    compact
      ? `${isActive ? "font-bold" : ""} block max-w-full truncate text-xs`
      : `${isActive ? "font-bold" : ""} text-base whitespace-nowrap`;

  return (
    <div className={`w-full ${className}`} data-testid={dataTestId}>
      {/* Tab Headers */}
      <div className={tabListClassName} data-testid={dataTestId ? `${dataTestId}-tablist` : undefined}>
        <nav className={`-mb-px flex ${compact ? "w-full gap-0.5" : ""}`} role="tablist">
          {tabs.map((tab, index) => (
            <button
              key={index}
              type="button"
              data-testid={dataTestId ? `${dataTestId}-tab-${index}` : undefined}
              onClick={() => handleTabChange(index)}
              className={tabButtonClassName(safeActiveTab === index)}
              role="tab"
              aria-selected={safeActiveTab === index}
              aria-controls={panelId(index)}
              id={tabId(index)}
            >
              <span className={tabLabelClassName(safeActiveTab === index)}>
                {tab?.label || `Tab ${index + 1}`}
              </span>
              {tab?.count && <span className={`ml-2 rounded-full px-2 py-0.5 text-xs`}>{tab.count}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div
        className={`tab-content ${compact ? "mt-2" : "mt-4"}`}
        role="tabpanel"
        data-testid={dataTestId ? `${dataTestId}-panel-${safeActiveTab}` : undefined}
        aria-labelledby={tabId(safeActiveTab)}
        id={panelId(safeActiveTab)}
      >
        {renderTabContent ? renderTabContent(safeActiveTab) : tabs[safeActiveTab]?.content}
      </div>
    </div>
  );
};

export default TabsContainer;
